import React, { useCallback, useEffect, useState } from "react";
import "./appSidebar.scss";
import { Menu, Tooltip } from "antd";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import AuthContainer from "../../../store/container/AuthContainer";
import { AppRoutes } from "../../../routes/routeConstants/appRoutes";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import ActionCable, { Cable, createConsumer } from "actioncable";
import { getHeaders } from "../../../interceptor/axiosInstance";
import { ScanCenter } from "../../../models/ScanCenter/scanCenter.model";
import ProfileService from "../../../services/Profile/profile.service";
import CaseContainer from "../../../store/container/CaseContainer";
import { CaseReducerProps } from "../../../store/reducers/caseReducer";
import { CaseUploadStatusEnum } from "../../../enums/caseUploadStatus.enum";
import { useHistory } from "react-router-dom";
import {
  LeftOutlined,
  MenuUnfoldOutlined,
  DatabaseFilled,
  UserOutlined,
  DiffTwoTone,
  MedicineBoxOutlined,
  TeamOutlined,
  SettingOutlined,
  ProfileOutlined,
  UsergroupAddOutlined,
  TagsOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons"; // Import Ant Design icons
import { CaseNotificationEvent } from "../../../enums/CaseNotificationEvent.enum";
import { CaseDetail } from "../../../models/CaseDetail/caseDetail.model";
import { deserialize } from "serializr";
import { AdminTypeEnum } from "../../../enums/AdminType.enum";
import { Doctor } from "../../../models/Doctor/doctor.model";
import { Admin } from "../../../models/Admin/admin.model";
import { SET_USER } from "../../../store/definitions/authConstants";
import { store } from "../../../store";
import { maybeRedirectToMatchingAppShell } from "../../utils/appShellRedirect";
import {
  setCaseAssignmentChanged,
  setReportDraftUpdated,
  setReportLockAcquired,
  setReportLockReleased,
} from "../../../store/actions/reportLockActions";
import { bumpCaseChatUnread } from "../../../store/actions/caseActions";
import { isOwnCaseChatSocketSender } from "../../../shared/utils/caseChatUnread";
import { pushInAppNotification } from "../../../store/actions/inAppNotificationActions";
import {
  playNotificationChime,
  warmNotificationAudio,
} from "../../../shared/utils/playNotificationChime";
import { isSoundNotificationsMuted } from "../../../shared/utils/soundPreferences";
import { InAppNotification } from "../../../types/inAppNotification";

const CASES_SEARCH_STORAGE_KEY = "casesSearch";

interface AppSidebarProps extends AuthReducerProps, CaseReducerProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

function AppSidebar({
  authenticated,
  userRole,
  user,
  setNewCaseCreated,
  setCaseDetailUpdated,
  setCaseDeleted,
  collapsed,
  setCollapsed,
  adminType,
}: AppSidebarProps) {
  const adminId = userRole === UserRoleEnum.ADMIN ? user?.id : user?.adminId;
  const history = useHistory();
  const [pathname, setPathname] = useState<string>(history.location.pathname);
  const [scanCenter, setScanCenter] = useState<ScanCenter>();
  //   const [collapsed, setCollapsed] = useState(false); // State for sidebar collapse
  //   const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const handleClick = (e: any) => {
    const currentPath = history.location.pathname;
    const currentSearch = history.location.search || "";

    if (currentPath === AppRoutes.HOME) {
      sessionStorage.setItem(CASES_SEARCH_STORAGE_KEY, currentSearch);
    }

    if (e.key === AppRoutes.HOME) {
      const savedSearch = sessionStorage.getItem(CASES_SEARCH_STORAGE_KEY) || "";
      history.push(AppRoutes.HOME + savedSearch);
    } else {
      history.push(e.key);
    }
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed); // Toggle sidebar collapse state
  };

  // const handleResize = () => {
  //   if (window.innerWidth < 1300) {
  //     setCollapsed(true); // Collapse sidebar on small screens
  //   } else {
  //     setCollapsed(false); // Expand sidebar on larger screens
  //   }
  // };

  // useEffect(() => {
  //   window.addEventListener('resize', handleResize);
  //   // Cleanup on component unmount
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  useEffect(() => {
    return history.listen((location) => {
      setPathname(location.pathname);
      if (location.pathname === AppRoutes.HOME) {
        sessionStorage.setItem(CASES_SEARCH_STORAGE_KEY, location.search || "");
      }
    });
  }, [history]);

  /** Unlocks Web Audio after first user gesture so socket-driven chimes can play. */
  useEffect(() => {
    const onFirstPointer = () => {
      warmNotificationAudio();
      window.removeEventListener("pointerdown", onFirstPointer, {
        capture: true,
      });
    };
    window.addEventListener("pointerdown", onFirstPointer, { capture: true });
    return () =>
      window.removeEventListener("pointerdown", onFirstPointer, {
        capture: true,
      });
  }, []);

  useEffect(() => {
    if (authenticated && userRole) {
      ProfileService.getProfile(
        userRole,
        (user: ScanCenter | Doctor | Admin) => {
          if (user instanceof ScanCenter) setScanCenter(user);
          store.dispatch({
            type: SET_USER,
            payload: {
              user,
            },
          });
          // INTENTIONALLY NOT redirecting based on the user's stored
          // `isNewApp` preference. Per product decision, the live origin
          // is the source of truth (landing on `app.radiolinq.com` →
          // Classic, `appv2.radiolinq.com` → New). The Classic/New toggle
          // in `AppHeader` is a session-time jump only; we never auto-
          // relocate the user on bootstrap. `isNewApp` is kept around as
          // a write-only analytics signal.
        },
        () => {},
        () => {}
      );
    }
  }, [authenticated, userRole]);

  useEffect(() => {
    let subscription: any;
    const headers = getHeaders();
    if (userRole && user?.id && headers["access-token"] && headers["client"] && headers["uid"]) {
      const cable = ActionCable.createConsumer(
        `${process.env.REACT_APP_SOCKET_URL}?access-token=${headers["access-token"]}&client=${headers["client"]}&uid=${headers["uid"]}`
      );
      subscription = cable.subscriptions.create(
        {
          channel: "NotificationsChannel",
          room: `notifications:${userRole}: ${user?.id}`,
        },
        {
          connected: () => {
            // console.log("connected");
          },
          disconnected: () => {
            console.log("disconnected");
          },
          received: (
            props: any
          ) => {
            const {
              event_type,
              case_details,
              report_id,
              case_id,
              locked_by,
              released_by,
              force_released,
              saved_by,
              saved_at,
              content,
              sender,
              notification,
            } = props;
            const caseDetailId = case_details?.case_detail_id ?? null;
            setTimeout(() => {
              switch (event_type) {
                case CaseNotificationEvent.APP_NOTIFICATION: {
                  const n = notification as InAppNotification | undefined;
                  if (n?.id != null) {
                    store.dispatch(pushInAppNotification(n));
                    if (!n.read_at && !isSoundNotificationsMuted(userRole, user?.id)) {
                      playNotificationChime();
                    }
                  }
                  break;
                }
                case CaseNotificationEvent.NEW_CASE_CREATED: {
                  setNewCaseCreated(caseDetailId);
                  break;
                }
                case CaseNotificationEvent.CASE_DETAIL_UPDATED:
                case CaseNotificationEvent.CASE_TAG_UPDATED:
                case CaseNotificationEvent.CASE_REOPENED: {
                  setCaseDetailUpdated(caseDetailId);
                  break;
                }
                case CaseNotificationEvent.CASE_CHAT_MESSAGE: {
                  const sid = case_details?.case_detail_id ?? case_id ?? null;
                  const openChatId =
                    store.getState().caseDetail.caseChatDrawerCaseId;
                  if (
                    sid != null &&
                    sid !== openChatId &&
                    !isOwnCaseChatSocketSender(userRole, user, sender)
                  ) {
                    store.dispatch(bumpCaseChatUnread(sid));
                  }
                  break;
                }
                case CaseNotificationEvent.CASE_ASSIGNMENT_STATUS_UPDATED:
                case CaseNotificationEvent.CASE_ASSIGNMENT_UPDATED: {
                  setCaseDetailUpdated(caseDetailId);
                  store.dispatch(setCaseAssignmentChanged(caseDetailId));
                  break;
                }
                case CaseNotificationEvent.ASSIGNMENT_REMOVED: {
                  setCaseDeleted(caseDetailId);
                  store.dispatch(setCaseAssignmentChanged(caseDetailId, true));
                  break;
                }
                case CaseNotificationEvent.REPORT_LOCK_ACQUIRED: {
                  // Always dispatch so the dashboard updates (including the acquirer's own dashboard)
                  store.dispatch(setReportLockAcquired(report_id, locked_by));
                  break;
                }
                case CaseNotificationEvent.REPORT_LOCK_RELEASED: {
                  store.dispatch(setReportLockReleased(report_id, !!force_released));
                  break;
                }
                case CaseNotificationEvent.REPORT_DRAFT_UPDATED: {
                  // Skip echo-back: don't update the editor of the user who saved the draft
                  if (saved_by?.id === user?.id && saved_by?.type === userRole) break;
                  store.dispatch(
                    setReportDraftUpdated({
                      reportId: report_id,
                      content: content ?? "",
                      savedBy: saved_by,
                      savedAt: saved_at,
                    })
                  );
                  break;
                }
              }
            }, 500);
          }
        }
      );
    }
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [userRole, user]);

  return authenticated ? (
    <div className={`app-sidebar ${collapsed ? "collapsed" : ""}`}>
      {/* Sidebar header with toggle button */}
      <div className="app-sidebar__header">
        {collapsed ? (
          <Tooltip title="Expand Menu">
            <MenuUnfoldOutlined
              onClick={toggleCollapsed}
              className="app-sidebar__toggle collapsed"
            />
          </Tooltip>
        ) : (
          <LeftOutlined
            onClick={toggleCollapsed}
            className="app-sidebar__toggle expanded"
          />
        )}
        {!collapsed && (
          <>
            {adminId === 2 ? (
              <>
                <h1 className="app-sidebar__logo">CONRAD</h1>
                <h5 className="app-sidebar__logo-subtext">TELERADIOLOGY</h5>
              </>
            ) : (
              <h1 className="app-sidebar__logo">RadiolinQ</h1>
            )}
          </>
        )}
        {!collapsed && (
          <div className="app-sidebar__role">
            {userRole?.split("_").join(" ")}
          </div>
        )}
      </div>

      {/* Sidebar Menu */}
      <Menu
        selectedKeys={[pathname]}
        onClick={handleClick}
        defaultSelectedKeys={[AppRoutes.HOME]}
        mode="inline"
        inlineCollapsed={collapsed} // Use collapsed state for the menu
      >
        <Menu.Item key={AppRoutes.HOME} icon={<DatabaseFilled />}>
          <div className="app-sidebar__menu-title">Cases</div>
        </Menu.Item>
        {adminType !== AdminTypeEnum.DIGITAL_SHARING_ADMIN &&
          userRole === UserRoleEnum.ADMIN && (
            <>
              <Menu.Item
                key={AppRoutes.SCAN_CENTRES}
                icon={<MedicineBoxOutlined />}
              >
                <div className="app-sidebar__menu-title">Scan Centers</div>
              </Menu.Item>
              <Menu.Item key={AppRoutes.DOCTORS} icon={<TeamOutlined />}>
                <div className="app-sidebar__menu-title">Doctors</div>
              </Menu.Item>
              <Menu.Item
                key={AppRoutes.REFERRING_DOCTORS}
                icon={<UsergroupAddOutlined />}
              >
                <div className="app-sidebar__menu-title">Referring Doctors</div>
              </Menu.Item>
                {
                    (userRole === UserRoleEnum.ADMIN) && (
                    <Menu.Item
                        key={AppRoutes.CUSTOM_TAGS}
                        icon={<TagsOutlined />}
                      >
                        <div className="app-sidebar__menu-title">Custom Tags</div>
                    </Menu.Item>
                    )
                }
            </>
          )}
        {adminType !== AdminTypeEnum.DIGITAL_SHARING_ADMIN &&
          (userRole === UserRoleEnum.DOCTOR ||
            userRole === UserRoleEnum.ADMIN) && (
            <>
              <Menu.Item key={AppRoutes.REPORT_TEMPLATES} icon={<DiffTwoTone />}>
                <div className="app-sidebar__menu-title">Report Templates</div>
              </Menu.Item>
              <Menu.Item key={AppRoutes.REPORT_MACROS} icon={<ThunderboltOutlined />}>
                <div className="app-sidebar__menu-title">Report Macros</div>
              </Menu.Item>
            </>
          )}
        <Menu.Item key={AppRoutes.PROFILE} icon={<SettingOutlined />}>
          <div className="app-sidebar__menu-title">Profile</div>
        </Menu.Item>
      </Menu>

      {adminId === 2 && (
        <div className="app-sidebar__footer">
          {!collapsed && (
            <h1>
              <span>Powered by</span> <br />
              RadiolinQ
            </h1>
          )}
        </div>
      )}
    </div>
  ) : null;
}

export default CaseContainer(AuthContainer(AppSidebar));
